import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';

// Module-level notification store: the single source of truth, always present
// and independent of any component lifecycle. notify() can fire from anywhere,
// any time — even before a container mounts (e.g. PaymentReturnToast's effect
// runs from inside the Router subtree, ahead of the container lower in the
// tree). NotificationContainer is just a subscribed view, so there is no
// mount-order coupling and no buffering special-case to maintain.
let notifications = [];
const listeners = new Set();

function setNotifications(next) {
    notifications = next;
    listeners.forEach(listener => listener(notifications));
}

/**
 * Show a toast notification.
 * @param {string} message - Text to display
 * @param {string} type - 'error' | 'success' | 'info'
 * @param {number|object} durationOrOpts - duration in ms OR {duration, action: {label, onClick}}
 */
export function notify(message, type = 'error', durationOrOpts = 5000) {
    let duration = 5000;
    let action = null;
    if (typeof durationOrOpts === 'number') {
        duration = durationOrOpts;
    } else if (durationOrOpts && typeof durationOrOpts === 'object') {
        duration = durationOrOpts.duration || 5000;
        action = durationOrOpts.action || null;
    }

    setNotifications([
        ...notifications,
        {message, type, duration, action, id: Date.now() + Math.random()},
    ]);
}

function dismissNotification(id) {
    setNotifications(notifications.filter(n => n.id !== id));
}

export function NotificationContainer() {
    const [items, setItems] = useState(notifications);

    useEffect(() => {
        listeners.add(setItems);
        // Pick up anything emitted between initial render and subscribe.
        setItems(notifications);
        return () => listeners.delete(setItems);
    }, []);

    if (items.length === 0) return null;

    return ReactDOM.createPortal(
        <div className="resume-notify">
            {items.map(item => (
                <NotificationItem key={item.id} item={item} onRemove={dismissNotification} />
            ))}
        </div>,
        document.body
    );
}

function NotificationItem({item, onRemove}) {
    const [leaving, setLeaving] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setLeaving(true), item.duration - 300);
        const removeTimer = setTimeout(() => onRemove(item.id), item.duration);
        return () => { clearTimeout(timer); clearTimeout(removeTimer); };
    }, []);

    const close = (e) => {
        e && e.stopPropagation();
        setLeaving(true);
        setTimeout(() => onRemove(item.id), 300);
    };

    const handleAction = (e) => {
        e.stopPropagation();
        if (item.action && typeof item.action.onClick === 'function') {
            item.action.onClick();
        }
        close();
    };

    return (
        <div
            className={`resume-notify__item resume-notify__item--${item.type} ${leaving ? 'resume-notify__item--leaving' : ''}`}
            onClick={close}
        >
            <div className="resume-notify__icon">
                {item.type === 'error' ? '\u2718' : item.type === 'success' ? '\u2714' : '\u2139'}
            </div>
            <div className="resume-notify__text">{item.message}</div>
            {item.action && (
                <button type="button" className="resume-notify__action" onClick={handleAction}>
                    {item.action.label || 'Undo'}
                </button>
            )}
        </div>
    );
}
